import type { ReactNode } from "react";
import type { DocumentKind } from "@pushback-and-chaos/contracts";

/**
 * A DOCUMENT KIND'S ICON (Andre, 2026-09-22): a small monochrome line drawing
 * at the top right of a desk card, across from the kind in words — an
 * envelope for a message, a grid for figures, a list for the index, a folded
 * corner for a note, and so on.
 *
 * It is decoration for the eye and nothing for the record: `aria-hidden`, no
 * title, no text, and the kind it stands for is never written into the
 * markup — `FINANCIAL_TABLE` in a DOM attribute is the same leak Law 2 bans on
 * the card's face. Fourteen-pixel strokes in the kind row's own ink (Codex,
 * 2026-09-22: printed symbols, never miniature graphite paintings), so the
 * icon sits with the type rather than over it.
 */
export function KindIcon({ kind }: { kind: DocumentKind }): ReactNode {
  return (
    <svg className="pcs-kind-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      {strokes(kind)}
    </svg>
  );
}

function strokes(kind: DocumentKind): ReactNode {
  switch (kind) {
    // An envelope.
    case "EMAIL":
      return (
        <>
          <rect x="1.5" y="3.5" width="13" height="9" rx="1" />
          <path d="M2 4.5l6 4.5 6-4.5" />
        </>
      );
    // A sheet ruled into a few cells (Codex's redraw, 2026-09-22: the nine-cell grid was dense).
    case "FINANCIAL_TABLE":
      return <path d="M2.5 2.5h11v11h-11z M2.5 6h11 M2.5 9.5h11 M6.5 2.5v11" />;
    // A list with short marks — stroke only, so the family stays one thing.
    case "DATAROOM_INDEX":
      return <path d="M2.5 4h1 M2.5 8h1 M2.5 12h1 M6 4h7.5 M6 8h7.5 M6 12h7.5" />;
    // A square note with a lifted corner.
    case "POSTIT":
      return (
        <>
          <path d="M2.5 2.5h11v7l-4 4h-7z" />
          <path d="M13.5 9.5h-4v4" />
        </>
      );
    // A page with its lines of type.
    case "MEMO":
      return (
        <>
          <path d="M3.5 1.5h6l3 3v10h-9z" />
          <path d="M9.5 1.5v3h3M5.5 8h5M5.5 10.5h5" />
        </>
      );
    // A chart sheet with equal bars: exhibit material, without inventing a
    // rising or falling result (Codex, 2026-09-22 — the picture read as a photograph).
    case "EXHIBIT":
      return <path d="M2 2.5h12v11H2z M4.5 5v6h7 M7 7v4 M10 7v4" />;
    // Two voices: a pair of quotation marks.
    case "TRANSCRIPT":
      return <path d="M2.5 3.5h4v4h-4z M6.5 7.5c0 2-1.5 3.5-3.5 4 M9.5 3.5h4v4h-4z M13.5 7.5c0 2-1.5 3.5-3.5 4" />;
    // A slide: a wide frame on a stand, the stem joined to its foot.
    case "SLIDE":
      return <path d="M1.5 3h13v8h-13z M8 11v3 M5.5 14h5" />;
    // An eye: what you saw.
    case "ENVIRONMENT":
      return (
        <>
          <path d="M1.5 8c2-3.5 4.2-5 6.5-5s4.5 1.5 6.5 5c-2 3.5-4.2 5-6.5 5S3.5 11.5 1.5 8z" />
          <circle cx="8" cy="8" r="2" />
        </>
      );
    // A plain sheet.
    default:
      return <path d="M3.5 1.5h9v13h-9z" />;
  }
}
